import { type ApolloCache, isReference } from '@apollo/client';

// Statuses the block_fsbo_users action takes down (moved back to
// pending_review). Same set as
// functions/src/hasura-actions/routes/block-fsbo-users/queries.ts.
export const LIVE_LOT_STATUSES: ReadonlySet<string> = new Set([
  'available',
  'available_coming_soon',
  'available_off_market',
]);

/**
 * Mirror what the block_fsbo_users action did server-side onto the Apollo
 * cache, so the listing page reflects it without a reload (#24256): every
 * blocked advertiser is flagged, the listing we are looking at drops from a
 * live status to pending_review, and its portal listings are all offline.
 * Other listings of the blocked accounts are refetched, not patched.
 */
export const markAdvertisersBlockedInCache = (
  cache: ApolloCache,
  {
    userIds,
    lotId,
    reason,
    batchId,
  }: {
    userIds: readonly string[];
    lotId: string | null;
    reason?: string | null;
    batchId: string;
  },
): void => {
  const now = new Date().toISOString();
  for (const userId of userIds) {
    cache.modify({
      id: cache.identify({ __typename: 'users', id: userId }),
      fields: {
        fsbo_blocked_at: () => now,
        fsbo_block_batch_id: () => batchId,
        ...(reason !== undefined
          ? { fsbo_blocked_reason: () => reason ?? null }
          : {}),
      },
    });
  }

  if (lotId == null) {
    return;
  }

  cache.modify({
    id: cache.identify({ __typename: 'lots', id: lotId }),
    fields: {
      status: (current: string) =>
        LIVE_LOT_STATUSES.has(current) ? 'pending_review' : current,
      // The action unpublishes every live portal listing of the lot; the
      // "Online" control reads `portal_listings[].live`, so flip each one.
      portal_listings: (current: unknown) => {
        for (const entry of Array.isArray(current) ? current : []) {
          if (isReference(entry)) {
            cache.modify({
              id: entry.__ref,
              fields: { live: () => false },
            });
          }
        }
        return current;
      },
    },
  });
};

/** The reverse: unblock_fsbo_users cleared the flag on these accounts. */
export const markAdvertisersUnblockedInCache = (
  cache: ApolloCache,
  userIds: readonly string[],
): void => {
  for (const userId of userIds) {
    cache.modify({
      id: cache.identify({ __typename: 'users', id: userId }),
      fields: {
        fsbo_blocked_at: () => null,
        fsbo_blocked_reason: () => null,
        fsbo_block_batch_id: () => null,
      },
    });
  }
};
